import { useEffect, useMemo, useState } from 'react';
import './FairQuiz.css';

const ORDER = ['trauma', 'mind', 'soul', 'body'];

const CHAPTERS = {
  trauma: {
    name: 'Healing the Trauma',
    blurb:
      'Explore your past, see how unresolved pain shapes your present, and learn to live with your scars in peace.',
  },
  mind: {
    name: 'Healing the Mind',
    blurb:
      'Take command of your inner dialogue and cultivate the clarity and resilience your journey needs.',
  },
  soul: {
    name: 'Healing the Soul',
    blurb:
      'Return to the core of who you are, beyond stories and scars, and nurture self-compassion.',
  },
  body: {
    name: 'Healing the Body',
    blurb:
      'Learn how your body holds and releases what you live inside, and find your grounding again.',
  },
};

const QUESTIONS = [
  {
    q: 'When things get hard, what do you notice first?',
    a: [
      { ch: 'trauma', text: 'Old memories or feelings I thought I had left behind' },
      { ch: 'mind', text: 'My thoughts racing or going in circles' },
      { ch: 'soul', text: 'A sense of being disconnected from who I am' },
      { ch: 'body', text: 'Tension, tiredness, or a heavy chest' },
    ],
  },
  {
    q: 'What do you wish came more easily?',
    a: [
      { ch: 'trauma', text: 'Making peace with what happened to me' },
      { ch: 'mind', text: 'Quieting my inner critic' },
      { ch: 'soul', text: 'Being kind to myself' },
      { ch: 'body', text: 'Feeling at home in my body' },
    ],
  },
  {
    q: 'Which sentence sounds most like you lately?',
    a: [
      { ch: 'trauma', text: "I keep reacting in ways I don't understand." },
      { ch: 'mind', text: 'I overthink everything.' },
      { ch: 'soul', text: 'I feel like something is missing.' },
      { ch: 'body', text: "I'm exhausted and I keep ignoring it." },
    ],
  },
  {
    q: 'What would a good day look like?',
    a: [
      { ch: 'trauma', text: 'Carrying my past without it weighing on me' },
      { ch: 'mind', text: 'A calm, clear head' },
      { ch: 'soul', text: 'Feeling connected to what matters to me' },
      { ch: 'body', text: 'Energy and rest in the right amounts' },
    ],
  },
  {
    q: 'What are you hardest on yourself about?',
    a: [
      { ch: 'trauma', text: "Not being 'over it' yet" },
      { ch: 'mind', text: 'Not thinking more positively' },
      { ch: 'soul', text: "Not knowing what I really want" },
      { ch: 'body', text: 'Not taking better care of myself' },
    ],
  },
  {
    q: 'What do you need most right now?',
    a: [
      { ch: 'trauma', text: 'Permission to heal at my own pace' },
      { ch: 'mind', text: 'Tools to calm my thoughts' },
      { ch: 'soul', text: 'A gentle reminder of my worth' },
      { ch: 'body', text: 'Practices that help me feel grounded' },
    ],
  },
];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Turns scores into percentages that always add up to exactly 100
function toResults(answers) {
  const scores = { trauma: 0, mind: 0, soul: 0, body: 0 };
  answers.forEach((c) => { scores[c] += 1; });
  const total = answers.length || 1;

  const rows = ORDER.map((id) => {
    const raw = (scores[id] / total) * 100;
    return { id, score: scores[id], pct: Math.floor(raw), rem: raw - Math.floor(raw) };
  });

  let left = 100 - rows.reduce((sum, r) => sum + r.pct, 0);
  [...rows].sort((x, y) => y.rem - x.rem).slice(0, left).forEach((r) => { r.pct += 1; });

  return rows.sort((x, y) => y.score - x.score || ORDER.indexOf(x.id) - ORDER.indexOf(y.id));
}

export default function FairQuiz({ open, onClose }) {
  const [run, setRun] = useState(0);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [picked, setPicked] = useState(null);

  // answers are shuffled on every new run so the order never favors a chapter
  // (`run` changes on each restart, which is what triggers the reshuffle)
  const questions = useMemo(
    () => QUESTIONS.map((q) => ({ ...q, a: shuffle(q.a) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [run]
  );

  const restart = () => {
    setRun((r) => r + 1);
    setStep(0);
    setAnswers([]);
    setPicked(null);
  };

  // fresh quiz every time it opens
  useEffect(() => {
    if (open) restart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Escape to close + lock page scroll
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const total = questions.length;
  const done = step >= total;

  const choose = (opt, idx) => {
    if (picked !== null) return;
    setPicked(idx);
    setTimeout(() => {
      setAnswers((a) => [...a, opt.ch]);
      setStep((s) => s + 1);
      setPicked(null);
    }, 280);
  };

  const goBack = () => {
    setStep((s) => Math.max(0, s - 1));
    setAnswers((a) => a.slice(0, -1));
  };

  let results = [];
  let tops = [];
  if (done) {
    results = toResults(answers);
    const max = results[0].score;
    tops = results.filter((r) => r.score === max);
  }

  return (
    <div className="quiz-overlay" onClick={onClose}>
      <div
        className="quiz-card"
        role="dialog"
        aria-modal="true"
        aria-label="Find your chapter"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="quiz-x" onClick={onClose} aria-label="Close">×</button>

        {!done ? (
          <>
            <p className="quiz-eyebrow">
              Question {step + 1} of {total}
            </p>
            <div className="quiz-progress" aria-hidden="true">
              <div
                className="quiz-progress-bar"
                style={{ width: `${(step / total) * 100}%` }}
              />
            </div>

            <div className="quiz-step" key={`${run}-${step}`}>
              <h3 className="quiz-question">{questions[step].q}</h3>
              <div className="quiz-options">
                {questions[step].a.map((opt, i) => (
                  <button
                    key={opt.ch}
                    className={`quiz-option ${picked === i ? 'is-picked' : ''}`}
                    onClick={() => choose(opt, i)}
                  >
                    {opt.text}
                  </button>
                ))}
              </div>
            </div>

            {step > 0 && (
              <button className="quiz-back" onClick={goBack}>← Back</button>
            )}
          </>
        ) : (
          <div className="quiz-result">
            <p className="quiz-eyebrow">Your result</p>
            <h2 className="quiz-headline">
              {tops.length === 1 ? (
                <>
                  Your chapter is{' '}
                  <span className="quiz-highlight">{CHAPTERS[tops[0].id].name}</span>
                </>
              ) : (
                <>
                  You're between{' '}
                  <span className="quiz-highlight">
                    {tops.map((t) => CHAPTERS[t.id].name).join(' and ')}
                  </span>
                </>
              )}
            </h2>

            <div className="quiz-bars">
              {results.map((r) => {
                const isTop = tops.some((t) => t.id === r.id);
                return (
                  <div className={`quiz-bar-row ${isTop ? 'is-top' : ''}`} key={r.id}>
                    <div className="quiz-bar-label">
                      <span>{CHAPTERS[r.id].name}</span>
                      <strong>{r.pct}%</strong>
                    </div>
                    <div className="quiz-bar-track">
                      <div className="quiz-bar-fill" style={{ '--w': `${r.pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {tops.map((t) => (
              <p className="quiz-blurb" key={t.id}>
                {CHAPTERS[t.id].blurb}
              </p>
            ))}

            <div className="quiz-actions">
              <button className="quiz-btn quiz-btn-main" onClick={onClose}>
                Back to the book
              </button>
              <button className="quiz-btn quiz-btn-soft" onClick={restart}>
                Retake the quiz
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}